import React, { useState, useEffect, useRef } from 'react';
import { Box, TextField, Chip, Button, Typography, CircularProgress, Alert } from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api/v1';

// Reusable "type a material, get Did-you-mean suggestions" field.
// Calling code owns the actual text value (via value/onChange) so it still
// works with existing customItemDescription state. This component only adds
// the suggestion layer + "add as new material" action on top.
const MaterialSuggestField = ({
  value,
  onChange, // (text) => void — same as before
  onSelectExisting, // (materialItem) => void — called when user picks a suggestion
  departmentId,
  expenseCategoryId,
  label = 'Or type custom description',
  placeholder = '',
  helperText = '',
  multiline = false,
  disabled = false
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dismissed, setDismissed] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newUnit, setNewUnit] = useState('');
  const [createError, setCreateError] = useState('');
  const [created, setCreated] = useState(false);
  const debounceRef = useRef(null);

  useEffect(() => {
    setDismissed(false);
    setCreated(false);
    setCreateError('');
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!value || value.trim().length < 3 || !departmentId || !expenseCategoryId) {
      setSuggestions([]);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      try {
        setLoading(true);
        const token = localStorage.getItem('token');
        const res = await axios.get(
          `${API_BASE_URL}/material-items/suggest`,
          {
            params: { q: value.trim(), department_id: departmentId, expense_category_id: expenseCategoryId },
            headers: { Authorization: `Bearer ${token}` }
          }
        );
        setSuggestions(res.data.data || []);
      } catch (err) {
        setSuggestions([]);
      } finally {
        setLoading(false);
      }
    }, 400);

    return () => clearTimeout(debounceRef.current);
  }, [value, departmentId, expenseCategoryId]);

  const handleSelect = (item) => {
    setSuggestions([]);
    setDismissed(true);
    onSelectExisting(item);
  };

  const handleCreateNew = async () => {
    if (!newUnit.trim()) { setCreateError('Unit is required'); return; }
    try {
      setCreating(true);
      setCreateError('');
      const token = localStorage.getItem('token');
      const res = await axios.post(
        `${API_BASE_URL}/material-items`,
        {
          item_name: value.trim(),
          department_id: departmentId,
          expense_category_id: expenseCategoryId,
          unit: newUnit.trim()
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCreated(true);
      setSuggestions([]);
      onSelectExisting(res.data.data);
    } catch (err) {
      setCreateError(err.response?.data?.message || 'Failed to create material item');
    } finally {
      setCreating(false);
    }
  };

  const showSuggestions = !dismissed && !created && suggestions.length > 0;
  const showAddNew = !dismissed && !created && value?.trim().length >= 3 && !loading;

  return (
    <Box>
      <TextField
        fullWidth
        multiline={multiline}
        rows={multiline ? 2 : undefined}
        label={label}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        helperText={helperText}
        disabled={disabled}
        InputProps={{
          endAdornment: loading ? <CircularProgress size={16} /> : null
        }}
      />

      {showSuggestions && (
        <Box sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">Did you mean one of these?</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
            {suggestions.map((s) => (
              <Chip
                key={s.id}
                label={s.itemName}
                onClick={() => handleSelect(s)}
                color="primary"
                variant="outlined"
                size="small"
              />
            ))}
            <Chip
              label={`No, this is different`}
              onClick={() => setDismissed(true)}
              size="small"
              variant="outlined"
            />
          </Box>
        </Box>
      )}

      {showAddNew && !showSuggestions && (
        <Box sx={{ mt: 1, p: 1.5, bgcolor: 'grey.50', borderRadius: 1 }}>
          <Typography variant="caption" color="text.secondary">
            No existing material matches "{value.trim()}".
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, mt: 1, alignItems: 'flex-start' }}>
            <TextField
              size="small"
              label="Unit"
              placeholder="e.g. Bags, Kg, Nos"
              value={newUnit}
              onChange={(e) => setNewUnit(e.target.value)}
              sx={{ width: 160 }}
            />
            <Button
              size="small"
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleCreateNew}
              disabled={creating}
            >
              {creating ? 'Adding...' : `Add "${value.trim()}" as new material`}
            </Button>
          </Box>
          {createError && <Alert severity="error" sx={{ mt: 1 }}>{createError}</Alert>}
        </Box>
      )}

      {created && (
        <Alert severity="success" sx={{ mt: 1 }}>
          Added as a new material item and selected.
        </Alert>
      )}
    </Box>
  );
};

export default MaterialSuggestField;